import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo } from 'solid-js'
import {
  getDateRangesForSimplePeriod,
  getDateRangesForCustomPeriod,
  formatDateToApiString,
  type SimplePeriodType,
  type CompareMode
} from '../../shared/helpers/date.functions'
import { useCorisEngagement } from '../../shared/providers/coris/coris-engagement.provider'
import { useUser } from '../../shared/providers/user-provider'
import { usePeriods } from '../../shared/providers/period.provider'
import PeriodSelector from '../shared/components/PeriodSelector'
import SummaryCard from '../shared/components/SummaryCard'
import CommunityChart from '../shared/components/CommunityChart'
import ChannelsTable from '../shared/components/ChannelsTable'
import TopPostsTable from '../shared/components/TopPostsTable'
import EmptyState from '../shared/components/EmptyState'
import { sticky } from '../../shared/directives/sticky'
import { SkeletonSummaryCard, SkeletonChart } from '../shared/components/Skeleton'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const Community: Component = () => {
  const { engagementPeriod, setEngagementPeriod } = usePeriods()
  const [channelsPage, setChannelsPage] = createSignal(1)
  const [postsPage, setPostsPage] = createSignal(1)
  const [sortByChannels, setSortByChannels] = createSignal<string>('views')
  const [sortOrderChannels, setSortOrderChannels] = createSignal<'asc' | 'desc'>('desc')
  const [sortByPosts, setSortByPosts] = createSignal<string>('views')
  const [sortOrderPosts, setSortOrderPosts] = createSignal<'asc' | 'desc'>('desc')
  const [isStuck, setIsStuck] = createSignal(false)

  const { getCommunity } = useCorisEngagement()
  const { organizationSlug } = useUser()

  const [communityData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        ...engagementPeriod(),
        channelsPage: channelsPage(),
        postsPage: postsPage(),
        sortByChannels: sortByChannels(),
        sortOrderChannels: sortOrderChannels(),
        sortByPosts: sortByPosts(),
        sortOrderPosts: sortOrderPosts(),
        organization
      }
    },
    async (params) => {
      if (!params) return null

      const dateRanges =
        params.selectedPeriod === 'custom' && params.customDateRange
          ? getDateRangesForCustomPeriod(params.customDateRange.from, params.customDateRange.to)
          : getDateRangesForSimplePeriod(params.selectedPeriod)

      let fromPrevious = dateRanges.fromPrevious
      let toPrevious = dateRanges.toPrevious

      if (params.compareMode === 'custom_range' && params.compareCustomRange) {
        fromPrevious = formatDateToApiString(params.compareCustomRange.from)
        toPrevious = formatDateToApiString(params.compareCustomRange.to)
      }

      return await getCommunity.execute({
        fromDate: dateRanges.fromDate,
        toDate: dateRanges.toDate,
        fromPrevious,
        toPrevious,
        sortByPosts: params.sortByPosts,
        sortOrderPosts: params.sortOrderPosts,
        sortByChannels: params.sortByChannels,
        sortOrderChannels: params.sortOrderChannels,
        postsPage: params.postsPage,
        pageSize: PAGE_SIZE,
        organization: params.organization
      })
    }
  )

  const isEmpty = createMemo(() => {
    const data = communityData()
    return (
      data &&
      data.channels.length === 0 &&
      data.posts.length === 0 &&
      data.summary.posts.current === 0 &&
      data.summary.views.current === 0 &&
      data.summary.comments.current === 0 &&
      data.summary.upvotes.current === 0
    )
  })

  const handlePeriodChange = (period: SimplePeriodType) => {
    setEngagementPeriod({ selectedPeriod: period })
    setChannelsPage(1)
    setPostsPage(1)
  }

  const handleCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ customDateRange: { from, to } })
    setChannelsPage(1)
    setPostsPage(1)
  }

  const handleCompareModeChange = (mode: CompareMode) => {
    setEngagementPeriod({ compareMode: mode })
  }

  const handleCompareCustomRangeChange = (from: Date, to: Date) => {
    setEngagementPeriod({ compareCustomRange: { from, to } })
  }

  const handleChannelsPageChange = (page: number) => {
    setChannelsPage(page)
  }

  const handlePostsPageChange = (page: number) => {
    setPostsPage(page)
  }

  const handleSortChannels = (column: string, direction: 'asc' | 'desc') => {
    setSortByChannels(column)
    setSortOrderChannels(direction)
    setChannelsPage(1)
  }

  const handleSortPosts = (column: string, direction: 'asc' | 'desc') => {
    setSortByPosts(column)
    setSortOrderPosts(direction)
    setPostsPage(1)
  }

  return (
    <div class="flex flex-col gap-4 px-6">
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 py-6 transition-colors duration-200 -mx-6 px-6"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent border-b border-transparent': !isStuck()
        }}
      >
        <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <PeriodSelector
            selectedPeriod={engagementPeriod().selectedPeriod}
            onPeriodChange={handlePeriodChange}
            customDateRange={engagementPeriod().customDateRange}
            onCustomRangeChange={handleCustomRangeChange}
            compareMode={engagementPeriod().compareMode}
            onCompareModeChange={handleCompareModeChange}
            compareCustomRange={engagementPeriod().compareCustomRange}
            onCompareCustomRangeChange={handleCompareCustomRangeChange}
          />
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8">
        <Show when={!communityData.loading && communityData() && isEmpty()}>
          <EmptyState />
        </Show>

        <Show when={!isEmpty()}>
          <Show
            when={communityData()?.summary}
            fallback={
              <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
                <SkeletonSummaryCard />
              </div>
            }
          >
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <SummaryCard
                title="ls_community_posts"
                value={communityData()!.summary.posts.current}
                change={communityData()!.summary.posts.diff}
              />
              <SummaryCard
                title="ls_community_views"
                value={communityData()!.summary.views.current}
                change={communityData()!.summary.views.diff}
              />
              <SummaryCard
                title="ls_community_comments"
                value={communityData()!.summary.comments.current}
                change={communityData()!.summary.comments.diff}
              />
              <SummaryCard
                title="ls_community_upvotes"
                value={communityData()!.summary.upvotes.current}
                change={communityData()!.summary.upvotes.diff}
              />
            </div>
          </Show>

          <Show when={communityData()?.timeSeries} fallback={<SkeletonChart legendItems={4} />}>
            <CommunityChart data={communityData()!.timeSeries} />
          </Show>

          <ChannelsTable
            channels={communityData()?.channels ?? []}
            totalChannels={communityData()?.channels?.length ?? 0}
            page={channelsPage}
            pageSize={PAGE_SIZE}
            onPageChange={handleChannelsPageChange}
            onSort={handleSortChannels}
            sortColumn={sortByChannels}
            sortDirection={sortOrderChannels}
            isLoading={communityData.state === 'unresolved' || communityData.loading}
          />

          <TopPostsTable
            posts={communityData()?.posts ?? []}
            totalPosts={communityData()?.totalPosts ?? 0}
            page={postsPage}
            pageSize={PAGE_SIZE}
            onPageChange={handlePostsPageChange}
            onSort={handleSortPosts}
            sortColumn={sortByPosts}
            sortDirection={sortOrderPosts}
            isLoading={communityData.state === 'unresolved' || communityData.loading}
          />
        </Show>
      </div>
    </div>
  )
}

export default Community
